/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { Suspense, useContext, useEffect, type ComponentType, type ReactNode } from "react";
import { Navigate, Outlet, useLocation, useNavigate, useSearchParams } from "react-router";
import { UserContext } from "../context/UserContext";
import AuthService from "../services/AuthService";
import { IS_PROD } from "../utils/constants";
import { clearLoginRedirect, peekLoginRedirect, storeLoginRedirect } from "../lib/loginRedirect";
import { PageLoading } from "../ui/LoadingOverlay";
import { caregiver, child, provider, status } from "./pages";
import { AUDIENCES, audienceOf, type Audience } from "./routeTable";

const NotAllowed = status("NotAllowed");
const MobileAppOnly = status("MobileAppOnly");

const SHELLS: Record<Audience, ComponentType> = {
  parent: caregiver("CaregiverShell"),
  child: child("ChildShell"),
  careTeam: provider("ProviderShell")
};

function useSession() {
  const session = useContext(UserContext);
  if (session === undefined) {
    throw new Error("Routing gates need a UserContextProvider above them");
  }
  return session;
}

/** Outside production, `?token=` on a public route signs the visitor in with that token. */
function useTokenShortcut() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  useEffect(() => {
    if (IS_PROD || token === null) return;
    AuthService.setAuthToken(decodeURIComponent(token));
  }, [token]);
}

/** Mail clients append the sentence's period to a pasted link; drop it rather than 404. */
export function useTrailingDotStrip() {
  const location = useLocation();
  const navigate = useNavigate();
  const url = `${location.pathname}${location.search}`;
  useEffect(() => {
    if (url.endsWith(".")) navigate(url.slice(0, -1), { replace: true });
  }, [url, navigate]);
}

/**
 * Authenticated, the user query is settled, and there is no user: either the token names an
 * account the query could not load (a stale session, or the gateway refused it), which ends the
 * session so the visitor lands on sign-in instead of an endless spinner, or the token carries no
 * account at all (an external referrer's one-time token), which is not a web-app account.
 */
function NoUser({ fallback }: { fallback: ReactNode }) {
  const hasAccount = AuthService.hasUserId();
  useEffect(() => {
    if (hasAccount) AuthService.logout();
  }, [hasAccount]);
  return hasAccount ? <PageLoading /> : <>{fallback}</>;
}

function SignInFirst() {
  const location = useLocation();
  const requested = `${location.pathname}${location.search}`;
  useEffect(() => storeLoginRedirect(requested), [requested]);
  return <Navigate to={{ pathname: "/login", search: location.search }} replace state={location.state} />;
}

function ResumeAfterLogin() {
  const { loading, user } = useSession();
  const redirect = peekLoginRedirect();
  useEffect(clearLoginRedirect, []);
  if (redirect !== null) return <Navigate to={redirect} replace />;
  if (loading) return <PageLoading />;
  if (!user) return <NoUser fallback={<NotAllowed />} />;
  const audience = audienceOf(user.accountType);
  return audience === undefined ? <MobileAppOnly /> : <Navigate to={AUDIENCES[audience].home} replace />;
}

/** `/` and `/login`: anonymous visitors see the page, signed-in ones resume where they were headed. */
export function PublicOnly() {
  useTokenShortcut();
  const { authenticated, loginInProgress } = useSession();
  return authenticated && !loginInProgress ? <ResumeAfterLogin /> : <Outlet />;
}

/** Layout route for a gated path group: sign-in first, then the shell of the visitor's audience. */
export function RequireAccount({ audiences }: { audiences: readonly Audience[] }) {
  const { authenticated, loading, user } = useSession();
  if (!authenticated) return <SignInFirst />;
  if (loading) return <PageLoading />;
  if (!user) return <NoUser fallback={<NotAllowed />} />;
  const audience = audienceOf(user.accountType);
  if (audience === undefined || !audiences.includes(audience)) return <NotAllowed />;
  const Shell = SHELLS[audience];
  return (
    <Suspense fallback={<PageLoading />}>
      <Shell />
    </Suspense>
  );
}

/** A path that shows a different page per audience; rendered inside `RequireAccount`, so the user is known. */
export function ByAudience(pages: Partial<Record<Audience, ReactNode>>) {
  const { user } = useSession();
  const audience = audienceOf(user.accountType);
  return <>{audience === undefined ? null : pages[audience]}</>;
}
